import { useState, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronDown, Check, Ban, RotateCcw } from 'lucide-react';
import { marginCorrectionsApi } from '../../services/marginCorrectionsApi';
import { useToast } from '../ui/Toast';
import type { CorrectionGroup } from '../../types/marginCorrections';

const fmt = (n: number, d = 2) => n.toLocaleString('de-CH', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDateTime = (s: string) => new Date(s).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
const email = (e: string) => { const at = e.indexOf('@'); return at > 0 ? e.slice(0, at) : e; };
const REASON_MIN = 5;

interface Props {
    company: string;
    canEdit: boolean;
    refreshKey: number;
    onDataChanged: () => void;
}

export function CorrectionsTab({ company, canEdit, refreshKey, onDataChanged }: Props) {
    const { toast } = useToast();
    const [groups, setGroups] = useState<CorrectionGroup[]>([]);
    const [loading, setLoading] = useState(false);
    const [includeCancelled, setIncludeCancelled] = useState(false);
    const [expanded, setExpanded] = useState<Set<number>>(new Set());
    const [cancelId, setCancelId] = useState<number | null>(null);
    const [cancelReason, setCancelReason] = useState('');
    const [cancelling, setCancelling] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await marginCorrectionsApi.listGroups({ company, includeCancelled, limit: 100 });
            setGroups(res.groups);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load corrections', 'error');
        } finally {
            setLoading(false);
        }
    }, [company, includeCancelled, toast]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const toggle = (id: number) => setExpanded(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const handleCancel = async () => {
        if (cancelId == null || cancelReason.trim().length < REASON_MIN) return;
        setCancelling(true);
        try {
            await marginCorrectionsApi.cancelGroup(cancelId, cancelReason.trim());
            toast(`Correction #${cancelId} cancelled`, 'success');
            setCancelId(null);
            setCancelReason('');
            load();
            onDataChanged();
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to cancel', 'error');
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5 text-sm text-gray-600">
                    <input type="checkbox" checked={includeCancelled} onChange={e => setIncludeCancelled(e.target.checked)} className="rounded" />
                    Show cancelled
                </label>
                <span className="text-sm text-gray-400">{groups.length} group(s)</span>
            </div>

            {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" /></div>}
            {!loading && groups.length === 0 && <p className="text-center text-gray-400 py-12">No corrections yet</p>}

            <div className="space-y-2">
                {groups.map(g => {
                    const isOpen = expanded.has(g.groupId);
                    return (
                        <div key={g.groupId} className={`border rounded-lg ${g.isActive ? '' : 'bg-gray-50 opacity-75'}`}>
                            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50" onClick={() => toggle(g.groupId)}>
                                {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                                <span className="font-mono text-sm text-gray-500">#{g.groupId}</span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-800 truncate" title={g.reason}>{g.reason}</p>
                                    <p className="text-xs text-gray-400">{email(g.createdBy)} · {fmtDateTime(g.createdAt)} · {g.lineCount} lines</p>
                                </div>
                                {/* The balance guarantee, made visible on every row. */}
                                <span className={`text-xs px-2 py-1 rounded font-medium flex items-center gap-1 ${g.isBalanced ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    <Check className="w-3 h-3" /> Σ {fmt(g.sumDeltaEUR)}
                                </span>
                                {!g.isActive && <span className="text-xs px-2 py-1 rounded bg-gray-200 text-gray-500">Cancelled</span>}
                                {canEdit && g.isActive && (
                                    <button onClick={e => { e.stopPropagation(); setCancelId(g.groupId); setCancelReason(''); }} className="text-gray-400 hover:text-red-500 p-1" title="Cancel correction">
                                        <Ban className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {isOpen && (
                                <div className="border-t px-4 py-3 space-y-2">
                                    {g.lines.map(l => (
                                        <div key={l.lineId} className="flex items-center justify-between text-sm">
                                            <div className="min-w-0">
                                                <span className="font-mono text-xs">{l.targetDocumentCode}</span>
                                                <span className="text-gray-500 ml-2 truncate">{l.customerName}</span>
                                                {l.lineNote && <span className="text-gray-400 italic ml-2 text-xs">— {l.lineNote}</span>}
                                            </div>
                                            <span className={`font-mono font-medium shrink-0 ml-3 ${l.deltaEUR > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {l.deltaEUR > 0 ? '+' : ''}{fmt(l.deltaEUR)} EUR
                                            </span>
                                        </div>
                                    ))}
                                    {!g.isActive && g.cancellationReason && (
                                        <div className="mt-2 pt-2 border-t text-xs text-gray-500 flex items-start gap-1.5">
                                            <RotateCcw className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                            <span>Cancelled by {email(g.cancelledBy || '')} · {g.cancellationReason}</span>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Cancel modal */}
            {cancelId != null && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onMouseDown={e => { if (e.target === e.currentTarget && !cancelling) setCancelId(null); }}>
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-md p-5">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">Cancel correction #{cancelId}</h3>
                        <p className="text-sm text-gray-500 mb-4">This removes both add-on rows from the reporting data. The original invoice margins are restored. A reason is required.</p>
                        <textarea
                            value={cancelReason}
                            onChange={e => setCancelReason(e.target.value)}
                            placeholder="Reason for cancellation (min 5 characters)…"
                            rows={3} maxLength={1000} autoFocus
                            className="w-full border rounded-md px-3 py-2 text-sm resize-none"
                        />
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setCancelId(null)} disabled={cancelling} className="px-4 py-2 text-sm text-gray-600 border rounded-md hover:bg-gray-50">Keep</button>
                            <button onClick={handleCancel} disabled={cancelReason.trim().length < REASON_MIN || cancelling} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40">
                                {cancelling ? 'Cancelling…' : 'Cancel correction'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
